import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { Button } from '@/components/ui/Button'
import { PageError } from '@/components/ui/PageAsyncState'
import { StatusBadge, type BadgeTone } from '@/components/ui/StatusBadge'
import { BomSheet, type BomSheetHardwareLine, type BomSheetWoodLine } from '@/components/boms'
import { useCan } from '@/features/auth/AuthContext'
import {
  computeBomIntakeStatusCounts,
  computeM3Deviations,
} from '@/features/boms/bomIntakeMetrics'
import {
  applyStandardizedNamesApi,
  commitPackagingSessionApi,
  commitSessionApi,
  createSessionApi,
  createSourceFileApi,
  discardSessionApi,
  parseBomExcelForIntake,
  patchLineApi,
  stageLinesApi,
  type BomIntakeStagingLine,
  type ParsedBomWorkbook,
} from '@/services/boms/intake'
import { materialService, type Material } from '@/services/materials'
import { standardNameService, type StandardName } from '@/services/standardNames'
import { matchStandardName } from '@/lib/nameNormalization'
import { assertWorkbookFileSize, validateWorkbookBytes } from '@/lib/safeWorkbook'

const inputClass =
  'rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2 py-1 text-xs outline-none focus:border-[var(--color-accent)]'

type Step = 'upload' | 'preview' | 'done'

const VALIDATION_TONE: Record<
  BomIntakeStagingLine['validation_status'],
  BadgeTone
> = {
  OK: 'success',
  NEEDS_MAPPING: 'warning',
  ERROR: 'danger',
}

function lineLabel(line: BomIntakeStagingLine): string {
  const raw = line.raw_data ?? {}
  if (line.line_kind === 'wood') {
    return String(raw.part_name ?? raw.name ?? '—')
  }
  return String(raw.name_raw ?? raw.name ?? '—')
}

export interface BomIntakePageProps {
  initialStep?: Step
  initialParsed?: ParsedBomWorkbook | null
  initialLines?: BomIntakeStagingLine[]
  initialSessionId?: string | null
  initialTab?: 'bom' | 'packaging' | 'standardize'
  initialStandardNames?: StandardName[]
}

export function BomIntakePage({
  initialStep = 'upload',
  initialParsed = null,
  initialLines = [],
  initialSessionId = null,
  initialTab = 'bom',
  initialStandardNames = [],
}: BomIntakePageProps = {}) {
  const navigate = useNavigate()
  const canWrite = useCan('bom.write')
  const [step, setStep] = useState<Step>(initialStep)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [parsed, setParsed] = useState<ParsedBomWorkbook | null>(initialParsed)
  const [filename, setFilename] = useState('')
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId)
  const [lines, setLines] = useState<BomIntakeStagingLine[]>(initialLines)
  const [materials, setMaterials] = useState<Material[]>([])
  const [standardNames, setStandardNames] = useState<StandardName[]>(initialStandardNames)
  const [selectedStandardizeLineIds, setSelectedStandardizeLineIds] = useState<
    Set<string>
  >(new Set())
  const [standardizeSuccessMessage, setStandardizeSuccessMessage] = useState<
    string | null
  >(null)
  const [activeTab, setActiveTab] = useState<'bom' | 'packaging' | 'standardize'>(
    initialTab,
  )

  const [selectedModes, setSelectedModes] = useState<('assembled' | 'kd')[]>(() => {
    if (initialParsed?.modes_detected && initialParsed.modes_detected.length > 0) {
      return initialParsed.modes_detected
    }
    return ['assembled', 'kd']
  })

  const [bomName, setBomName] = useState(
    initialParsed?.name_hint || initialParsed?.code_hint || '',
  )
  const [packagingBomName, setPackagingBomName] = useState(
    initialParsed?.code_hint
      ? `${initialParsed.code_hint} - Đóng gói`
      : 'Bộ đóng gói',
  )
  const [packagingSuccess, setPackagingSuccess] = useState<{
    id: string
    name: string
  } | null>(null)

  useEffect(() => {
    void materialService.list().then(setMaterials).catch(() => setMaterials([]))
    void standardNameService
      .list()
      .then((res) => setStandardNames(res.items))
      .catch(() => setStandardNames([]))
  }, [])

  const standardizedPreviewLines = useMemo(() => {
    return lines.map((line) => {
      const raw = (line.raw_data ?? {}) as Record<string, unknown>
      const rawName =
        line.line_kind === 'wood'
          ? String(raw.part_name ?? raw.name ?? '')
          : String(raw.name_raw ?? raw.name ?? '')

      const match = matchStandardName(rawName, standardNames)
      const proposed = match.proposed_name || rawName
      return {
        lineId: line.id,
        lineKind: line.line_kind,
        rawName,
        proposedName: proposed,
        confidence: match.confidence,
        standardNameId: match.standard_name_id,
        hasChange: proposed.toLowerCase() !== rawName.toLowerCase(),
      }
    })
  }, [lines, standardNames])

  // Automatically preselect CONFIDENT changed lines whenever lines or standard names change
  useEffect(() => {
    const confidentChangedIds = new Set(
      standardizedPreviewLines
        .filter((item) => item.confidence === 'CONFIDENT' && item.hasChange)
        .map((item) => item.lineId),
    )
    setSelectedStandardizeLineIds(confidentChangedIds)
  }, [standardizedPreviewLines])

  const counts = useMemo(
    () => computeBomIntakeStatusCounts(lines),
    [lines],
  )

  const m3Deviations = useMemo(
    () => computeM3Deviations(lines),
    [lines],
  )

  const packagingLines = useMemo(
    () => lines.filter((l) => l.line_kind === 'packaging'),
    [lines],
  )

  const canCommitBom =
    lines.length > 0 &&
    counts.ERROR === 0 &&
    selectedModes.length > 0

  async function onFile(file: File | null) {
    if (!file) return
    setError(null)
    setBusy(true)
    try {
      assertWorkbookFileSize(file.size)
      const buf = await file.arrayBuffer()
      validateWorkbookBytes(file.name, new Uint8Array(buf))
      const doc = parseBomExcelForIntake(buf)
      if (doc.lines.length === 0) {
        throw new Error('Không parse được dòng nào từ file.')
      }
      setParsed(doc)
      setFilename(file.name)
      setBomName(doc.name_hint || doc.code_hint || file.name)
      setPackagingBomName(
        doc.code_hint
          ? `${doc.code_hint} - Đóng gói`
          : `${file.name.replace(/\.[^/.]+$/, '')} - Đóng gói`,
      )
      const detected: ('assembled' | 'kd')[] =
        doc.modes_detected && doc.modes_detected.length > 0
          ? doc.modes_detected
          : ['assembled', 'kd']
      setSelectedModes(detected)

      const source = await createSourceFileApi({
        filename: file.name,
        content_meta: {
          size_bytes: file.size,
          sheet_names: doc.sheet_names,
          line_count: doc.lines.length,
        },
      })
      const session = await createSessionApi({
        source_file_id: source.id,
        code_hint: doc.code_hint || null,
      })
      const staged = await stageLinesApi(session.id, doc.lines)
      setSessionId(session.id)
      setLines(staged)
      setStep('preview')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Parse/staging thất bại')
    } finally {
      setBusy(false)
    }
  }

  async function mapMaterial(lineId: string, materialId: string) {
    if (!sessionId) return
    setBusy(true)
    setError(null)
    try {
      const updated = await patchLineApi(sessionId, lineId, {
        material_id: materialId || null,
      })
      setLines((prev) => prev.map((l) => (l.id === lineId ? updated : l)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Map Material thất bại')
    } finally {
      setBusy(false)
    }
  }

  async function onCommitBom() {
    if (!sessionId || !canCommitBom) return
    setBusy(true)
    setError(null)
    try {
      const result = await commitSessionApi(sessionId, {
        name: bomName || null,
        modes: selectedModes,
      })
      setStep('done')
      if (result.boms && result.boms.length > 0) {
        navigate(`/purchasing/boms/${result.boms[0].id}`)
      } else {
        navigate(`/purchasing/boms/${result.bom.id}`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Commit thất bại')
    } finally {
      setBusy(false)
    }
  }

  async function onCommitPackaging() {
    if (!sessionId || packagingLines.length === 0) return
    setBusy(true)
    setError(null)
    try {
      const result = await commitPackagingSessionApi(sessionId, {
        name: packagingBomName || null,
      })
      setPackagingSuccess({
        id: result.packaging_bom.id,
        name: result.packaging_bom.name,
      })
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Tạo Packaging BOM thất bại',
      )
    } finally {
      setBusy(false)
    }
  }

  async function onDiscard() {
    if (!sessionId) {
      navigate('/purchasing/boms')
      return
    }
    setBusy(true)
    try {
      await discardSessionApi(sessionId)
      navigate('/purchasing/boms')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Discard thất bại')
    } finally {
      setBusy(false)
    }
  }

  function handleToggleStandardizeLine(lineId: string) {
    setSelectedStandardizeLineIds((prev) => {
      const next = new Set(prev)
      if (next.has(lineId)) {
        next.delete(lineId)
      } else {
        next.add(lineId)
      }
      return next
    })
  }

  function handleToggleAllStandardize() {
    const candidateLines = standardizedPreviewLines.filter(
      (item) => item.confidence !== 'UNKNOWN' && item.hasChange,
    )
    const allSelected =
      candidateLines.length > 0 &&
      candidateLines.every((item) =>
        selectedStandardizeLineIds.has(item.lineId),
      )
    if (allSelected) {
      setSelectedStandardizeLineIds(new Set())
    } else {
      setSelectedStandardizeLineIds(
        new Set(candidateLines.map((item) => item.lineId)),
      )
    }
  }

  async function handleApplyStandardization() {
    const toApply = standardizedPreviewLines.filter(
      (item) => selectedStandardizeLineIds.has(item.lineId) && item.hasChange,
    )
    if (toApply.length === 0) return

    setBusy(true)
    setError(null)
    setStandardizeSuccessMessage(null)

    const changes = toApply.map((item) => ({
      line_id: item.lineId,
      proposed_name: item.proposedName,
    }))

    try {
      if (sessionId) {
        const updated = await applyStandardizedNamesApi(sessionId, changes)
        setLines(updated)
      } else {
        // Fallback local update when testing or sessionless
        setLines((prev) =>
          prev.map((l) => {
            const change = changes.find((c) => c.line_id === l.id)
            if (!change) return l
            const raw = { ...((l.raw_data ?? {}) as Record<string, unknown>) }
            if (l.line_kind === 'wood') {
              raw.part_name = change.proposed_name
              if (raw.name !== undefined) raw.name = change.proposed_name
            } else {
              raw.name_raw = change.proposed_name
              if (raw.name !== undefined) raw.name = change.proposed_name
            }
            return { ...l, raw_data: raw }
          }),
        )
      }
      setStandardizeSuccessMessage(
        `Đã áp dụng chuẩn hóa thành công cho ${toApply.length} dòng!`,
      )
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Áp dụng chuẩn hóa tên thất bại',
      )
    } finally {
      setBusy(false)
    }
  }

  function handleSkipStandardization() {
    setActiveTab('bom')
  }

  // Prepared sheet lines for BomSheet preview
  const woodSheetLines: BomSheetWoodLine[] = useMemo(() => {
    return lines
      .filter((l) => l.line_kind === 'wood')
      .map((l, idx) => {
        const raw = (l.raw_data ?? {}) as Record<string, unknown>
        const mat = materials.find((m) => m.id === l.material_id)
        const t =
          raw.dims_finished_t !== undefined
            ? Number(raw.dims_finished_t)
            : null
        const w =
          raw.dims_finished_w !== undefined
            ? Number(raw.dims_finished_w)
            : null
        const len =
          raw.dims_finished_l !== undefined
            ? Number(raw.dims_finished_l)
            : null
        return {
          id: l.id,
          sort_order: idx + 1,
          part_name: String(
            raw.part_name ?? raw.name ?? `Chi tiết ${idx + 1}`,
          ),
          wood_type_raw: raw.wood_type_raw ? String(raw.wood_type_raw) : null,
          material_name: mat ? mat.name : null,
          dims_finished_t: t,
          dims_finished_w: w,
          dims_finished_l: len,
          tenon: raw.tenon ? String(raw.tenon) : null,
          qty_per: Number(raw.qty_per) || 1,
          veneer_spec: raw.veneer_spec ? String(raw.veneer_spec) : null,
          notes: raw.notes ? String(raw.notes) : null,
        }
      })
  }, [lines, materials])

  function getHardwareSheetLinesForMode(
    mode: 'assembled' | 'kd',
  ): BomSheetHardwareLine[] {
    return lines
      .filter((l) => l.line_kind === 'hardware')
      .filter(
        (l) =>
          !l.packaging_assembly_type || l.packaging_assembly_type === mode,
      )
      .map((l, idx) => {
        const raw = (l.raw_data ?? {}) as Record<string, unknown>
        const mat = materials.find((m) => m.id === l.material_id)
        return {
          id: l.id,
          sort_order: idx + 1,
          name_raw: String(raw.name_raw ?? raw.name ?? `Ngũ kim ${idx + 1}`),
          spec_raw: raw.spec_raw ? String(raw.spec_raw) : null,
          material_name: mat ? mat.name : null,
          qty_per: Number(raw.qty_per) || 1,
          uom_hint: raw.uom_hint ? String(raw.uom_hint) : 'Cái',
          color_hint: raw.color_hint ? String(raw.color_hint) : null,
          notes: raw.notes ? String(raw.notes) : null,
        }
      })
  }

  return (
    <div className="space-y-4">
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Định mức (BOM)', to: '/purchasing/boms' },
          { label: 'Import Excel' },
        ]}
      />
      <PageHeader
        title="Import BOM từ Excel"
        subtitle="Parse client-side → staging → chọn Mode (SX/KD) & Tách đóng gói → commit BOM inactive"
      />

      {error ? <PageError message={error} /> : null}

      {step === 'upload' ? (
        <section className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
          <p className="mb-3 text-sm text-slate-600">
            Chọn file MPxxx.xlsm. Hệ thống tự động bóc tách: BOM sản phẩm (Gỗ +
            Ngũ kim phân theo mode SX/KD) và Bộ đóng gói riêng biệt. M³ tinh
            được tự tính từ kích thước tinh Dày × Rộng × Dài ÷ 10⁹.
          </p>
          <input
            type="file"
            accept=".xlsm,.xlsx"
            disabled={busy}
            onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
          />
          {busy ? (
            <p className="mt-2 text-xs text-slate-500">Đang parse & staging…</p>
          ) : null}
        </section>
      ) : null}

      {step === 'preview' && parsed ? (
        <div className="space-y-4">
          <section className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-xs shadow-[var(--shadow-level-1)]">
            <div className="mb-3 flex flex-wrap gap-3 text-slate-700">
              {filename ? (
                <span>
                  File: <strong>{filename}</strong>
                </span>
              ) : null}
              <span>
                Code hint: <strong>{parsed.code_hint || '—'}</strong>
              </span>
              <span>
                Tên phát hiện: <strong>{parsed.name_hint || '—'}</strong>
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
              <KpiCard label="OK" value={counts.OK} tone="success" />
              <KpiCard
                label="NEEDS_MAPPING"
                value={counts.NEEDS_MAPPING}
                tone={counts.NEEDS_MAPPING ? 'warning' : 'default'}
              />
              <KpiCard
                label="ERROR"
                value={counts.ERROR}
                tone={counts.ERROR ? 'danger' : 'default'}
              />
              <KpiCard
                label="Đóng gói tách riêng"
                value={`${packagingLines.length} dòng`}
                tone={packagingLines.length ? 'default' : 'default'}
              />
            </div>
          </section>

          {/* M3 Deviation Warning */}
          {m3Deviations.deviatedCount > 0 ? (
            <div className="rounded-[var(--radius-md)] border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 shadow-[var(--shadow-level-1)]">
              <p className="font-semibold">
                ⚠ Cảnh báo: Có {m3Deviations.deviatedCount} dòng gỗ có M³ trong
                file Excel lệch &gt; 5% so với tính từ kích thước tinh. Hệ thống
                sẽ tự động dùng M³ tinh tự tính (Q-B3).
              </p>
              <ul className="mt-1.5 list-inside list-disc space-y-0.5 text-xs text-amber-800">
                {m3Deviations.deviations.slice(0, 3).map((d, i) => {
                  const raw = (d.line.raw_data ?? {}) as Record<string, unknown>
                  return (
                    <li key={i}>
                      {String(raw.part_name ?? raw.name ?? 'Dòng')}: Excel{' '}
                      <strong>{d.excelM3.toFixed(4)}</strong> m³ vs Tự tính{' '}
                      <strong>{d.calcM3.toFixed(4)}</strong> m³ (lệch{' '}
                      {d.diffPercent.toFixed(1)}%)
                    </li>
                  )
                })}
                {m3Deviations.deviatedCount > 3 ? (
                  <li>...và {m3Deviations.deviatedCount - 3} dòng khác</li>
                ) : null}
              </ul>
            </div>
          ) : null}

          {/* Tab navigation */}
          <div className="flex border-b border-slate-200">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'bom'
                  ? 'border-indigo-600 text-indigo-600 font-semibold'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setActiveTab('bom')}
            >
              1. BOM Sản phẩm (Gỗ &amp; Ngũ kim)
            </button>
            <button
              type="button"
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'packaging'
                  ? 'border-indigo-600 text-indigo-600 font-semibold'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setActiveTab('packaging')}
            >
              <span>2. Bộ Đóng gói (Packaging BOM)</span>
              {packagingLines.length > 0 ? (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 font-mono">
                  {packagingLines.length}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'standardize'
                  ? 'border-indigo-600 text-indigo-600 font-semibold'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setActiveTab('standardize')}
            >
              <span>3. Xem trước chuẩn hóa tên</span>
              {standardizedPreviewLines.filter((l) => l.hasChange).length > 0 ? (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 font-mono font-semibold">
                  {standardizedPreviewLines.filter((l) => l.hasChange).length}
                </span>
              ) : null}
            </button>
          </div>

          {/* TAB 1: PRODUCT BOM BY MODE */}
          {activeTab === 'bom' ? (
            <div className="space-y-4">
              {/* Mode Selection & BOM Name */}
              <section className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)] space-y-3 text-xs">
                <div className="flex flex-wrap items-center gap-6">
                  <div>
                    <span className="font-semibold text-slate-800 mr-2">
                      Chọn chế độ tạo BOM:
                    </span>
                    <label className="inline-flex items-center gap-1.5 mr-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedModes.includes('assembled')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedModes((prev) => [...prev, 'assembled'])
                          } else {
                            setSelectedModes((prev) =>
                              prev.filter((m) => m !== 'assembled'),
                            )
                          }
                        }}
                      />
                      <span>Ráp chết (assembled)</span>
                    </label>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedModes.includes('kd')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedModes((prev) => [...prev, 'kd'])
                          } else {
                            setSelectedModes((prev) =>
                              prev.filter((m) => m !== 'kd'),
                            )
                          }
                        }}
                      />
                      <span>Tháo rời (kd)</span>
                    </label>
                  </div>

                  <label className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800">
                      Tên BOM:
                    </span>
                    <input
                      className={`${inputClass} w-64`}
                      value={bomName}
                      onChange={(e) => setBomName(e.target.value)}
                    />
                  </label>
                </div>
                {selectedModes.length === 0 ? (
                  <p className="text-rose-600 font-medium">
                    ⚠ Phải chọn ít nhất một chế độ lắp ráp để tạo BOM.
                  </p>
                ) : null}
              </section>

              {/* Material Mapping Table */}
              <section className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
                <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-700">
                    Ánh xạ vật tư (Material Mapping) — {lines.filter((l) => l.line_kind !== 'packaging').length} dòng
                  </span>
                  {counts.NEEDS_MAPPING > 0 ? (
                    <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                      Còn {counts.NEEDS_MAPPING} dòng chưa map Material (vẫn được commit)
                    </span>
                  ) : null}
                </div>
                <table className="min-w-full text-left text-xs">
                  <thead className="sticky top-0 z-10 border-b border-[var(--color-border)] bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                    <tr>
                      <th className="px-2 py-2">Loại</th>
                      <th className="px-2 py-2">Tên / Chi tiết</th>
                      <th className="px-2 py-2">Chế độ</th>
                      <th className="px-2 py-2">Trạng thái</th>
                      <th className="px-2 py-2">Vật tư liên kết (Material)</th>
                      <th className="px-2 py-2">Ghi chú</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines
                      .filter((l) => l.line_kind !== 'packaging')
                      .map((line) => (
                        <tr key={line.id} className="border-t border-slate-100">
                          <td className="px-2 py-1 font-mono">{line.line_kind}</td>
                          <td className="px-2 py-1">{lineLabel(line)}</td>
                          <td className="px-2 py-1 font-mono">
                            {line.line_kind === 'wood'
                              ? 'Chung'
                              : line.packaging_assembly_type ?? 'Chung'}
                          </td>
                          <td className="px-2 py-1">
                            <StatusBadge
                              tone={VALIDATION_TONE[line.validation_status]}
                              label={line.validation_status}
                              showDot={false}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <select
                              className={`${inputClass} max-w-[14rem]`}
                              value={line.material_id ?? ''}
                              disabled={busy}
                              onChange={(e) =>
                                void mapMaterial(line.id, e.target.value)
                              }
                            >
                              <option value="">— chưa map —</option>
                              {materials.map((m) => (
                                <option key={m.id} value={m.id}>
                                  {m.name} ({m.uom_default || '—'})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1 text-slate-500">
                            {line.validation_notes ?? ''}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </section>

              {/* Preview BomSheets for selected modes */}
              <div className="space-y-6">
                <h3 className="text-sm font-semibold text-slate-800">
                  Xem trước Phiếu Định Mức (BOM Sheet) theo chế độ đã chọn:
                </h3>
                {selectedModes.map((mode) => (
                  <div
                    key={mode}
                    className="rounded-xl border border-slate-200 overflow-hidden shadow-sm"
                  >
                    <div className="bg-slate-100 px-4 py-2 font-semibold text-xs text-slate-700 flex justify-between items-center">
                      <span>
                        Chế độ:{' '}
                        {mode === 'assembled'
                          ? 'RÁP CHẾT (FA)'
                          : 'Tháo rời (KD)'}
                      </span>
                      <span className="text-xs font-normal text-slate-500">
                        {woodSheetLines.length} dòng gỗ ·{' '}
                        {getHardwareSheetLinesForMode(mode).length} dòng ngũ kim
                      </span>
                    </div>
                    <BomSheet
                      itemCode={parsed.code_hint || 'BOM'}
                      productName={parsed.name_hint || bomName || 'Sản phẩm'}
                      assemblyType={mode}
                      bomCode={
                        selectedModes.length > 1
                          ? `${bomName} (${mode === 'assembled' ? 'Ráp chết' : 'Tháo rời'})`
                          : bomName
                      }
                      status="inactive"
                      woodLines={woodSheetLines}
                      hardwareLines={getHardwareSheetLinesForMode(mode)}
                    />
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-slate-200">
                <Button
                  disabled={busy || !canCommitBom || !canWrite}
                  onClick={() => void onCommitBom()}
                  className={!canWrite ? 'opacity-50 cursor-not-allowed' : undefined}
                  title={!canWrite ? 'Cần quyền bom.write để tạo BOM' : undefined}
                >
                  {busy
                    ? 'Đang commit…'
                    : `Commit → Tạo ${selectedModes.length} BOM nháp (inactive)`}
                </Button>
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => void onDiscard()}
                >
                  Huỷ session
                </Button>
                {!canWrite ? (
                  <span className="text-xs text-slate-400 italic">
                    (Bạn không có quyền bom.write để tạo BOM)
                  </span>
                ) : !canCommitBom ? (
                  <span className="text-xs text-rose-600">
                    {selectedModes.length === 0
                      ? 'Vui lòng chọn ít nhất một chế độ tạo BOM.'
                      : counts.ERROR > 0
                        ? 'Có dòng bị lỗi (ERROR), vui lòng kiểm tra lại.'
                        : 'Không có dòng hợp lệ để commit.'}
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* TAB 2: PACKAGING BOM */}
          {activeTab === 'packaging' ? (
            <div className="space-y-4">
              {packagingSuccess ? (
                <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 text-xs text-emerald-800">
                  <p className="font-semibold text-sm">
                    Đã tạo Packaging BOM thành công!
                  </p>
                  <p className="mt-1">
                    Tên:{' '}
                    <strong>{packagingSuccess.name}</strong>
                  </p>
                  <div className="mt-3">
                    <Link
                      to={`/purchasing/packaging-boms/${packagingSuccess.id}`}
                      className="inline-flex items-center px-3 py-1.5 rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700 text-xs"
                    >
                      Xem chi tiết Packaging BOM →
                    </Link>
                  </div>
                </div>
              ) : null}

              {packagingLines.length === 0 ? (
                <section className="rounded-xl border border-dashed border-slate-200 p-8 text-center bg-slate-50">
                  <p className="text-sm text-slate-500">
                    File Excel này không chứa dòng đóng gói riêng biệt.
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Bộ đóng gói có thể tạo độc lập tại trang Quản lý Đóng gói
                    sau này.
                  </p>
                </section>
              ) : (
                <div className="space-y-4">
                  <section className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)] space-y-3 text-xs">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <label className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">
                          Tên Packaging BOM:
                        </span>
                        <input
                          className={`${inputClass} w-72`}
                          value={packagingBomName}
                          onChange={(e) => setPackagingBomName(e.target.value)}
                        />
                      </label>
                      <div className="flex items-center gap-2">
                        <Button
                          disabled={busy || !canWrite}
                          onClick={() => void onCommitPackaging()}
                          className={!canWrite ? 'opacity-50 cursor-not-allowed' : undefined}
                          title={!canWrite ? 'Cần quyền bom.write để tạo Packaging BOM' : undefined}
                        >
                          {busy ? 'Đang tạo…' : 'Tạo Packaging BOM nháp'}
                        </Button>
                        {!canWrite ? (
                          <span className="text-xs text-slate-400 italic">
                            (Cần quyền bom.write)
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </section>

                  <section className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
                    <div className="p-3 border-b border-slate-100 font-semibold text-xs text-slate-700">
                      Danh sách quy cách đóng gói bóc tách ({packagingLines.length} dòng)
                    </div>
                    <table className="min-w-full text-left text-xs">
                      <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                        <tr>
                          <th className="px-3 py-2">STT</th>
                          <th className="px-3 py-2">Tên vật tư đóng gói</th>
                          <th className="px-3 py-2">Quy cách kỹ thuật</th>
                          <th className="px-3 py-2 text-right">Số lượng</th>
                          <th className="px-3 py-2">ĐVT</th>
                          <th className="px-3 py-2">Ghi chú</th>
                        </tr>
                      </thead>
                      <tbody>
                        {packagingLines.map((line, idx) => {
                          const raw = (line.raw_data ?? {}) as Record<
                            string,
                            unknown
                          >
                          return (
                            <tr
                              key={line.id}
                              className="border-t border-slate-100"
                            >
                              <td className="px-3 py-1.5 text-slate-400 font-mono">
                                {idx + 1}
                              </td>
                              <td className="px-3 py-1.5 font-medium text-slate-900">
                                {String(
                                  raw.name_raw ??
                                    raw.name ??
                                    `Đóng gói ${idx + 1}`,
                                )}
                              </td>
                              <td className="px-3 py-1.5 text-slate-600">
                                {String(raw.spec_raw ?? '—')}
                              </td>
                              <td className="px-3 py-1.5 text-right font-mono">
                                {Number(raw.qty_per ?? 1)}
                              </td>
                              <td className="px-3 py-1.5 text-slate-600">
                                {String(raw.uom_hint ?? 'Bộ')}
                              </td>
                              <td className="px-3 py-1.5 text-slate-500">
                                {String(raw.notes ?? '—')}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </section>
                </div>
              )}
            </div>
          ) : null}

          {/* TAB 3: STANDARDIZE PREVIEW */}
          {activeTab === 'standardize' ? (
            <div className="space-y-4">
              {standardizeSuccessMessage ? (
                <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 text-xs text-emerald-800">
                  <p className="font-semibold text-sm">
                    {standardizeSuccessMessage}
                  </p>
                  <p className="mt-1 text-emerald-700">
                    Bạn có thể chuyển về tab BOM hoặc Đóng gói để tiếp tục commit.
                  </p>
                </div>
              ) : null}

              <section className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)] space-y-3 text-xs">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-sm text-slate-900">
                      Rà soát và chuẩn hóa tên vật tư từ Excel
                    </h3>
                    <p className="text-slate-500 text-xs mt-0.5">
                      Đối chiếu tên trong file Excel với từ điển tên chuẩn. Tích chọn dòng bạn muốn cập nhật trước khi commit BOM. Dòng &ldquo;Không rõ&rdquo; giữ nguyên tên gốc và không chặn commit.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      onClick={handleSkipStandardization}
                      disabled={busy}
                    >
                      Bỏ qua chuẩn hóa
                    </Button>
                    <Button
                      disabled={
                        busy ||
                        !canWrite ||
                        standardizedPreviewLines.filter(
                          (l) => selectedStandardizeLineIds.has(l.lineId) && l.hasChange,
                        ).length === 0
                      }
                      onClick={() => void handleApplyStandardization()}
                    >
                      {busy
                        ? 'Đang áp dụng…'
                        : `Áp dụng chuẩn hóa (${
                            standardizedPreviewLines.filter(
                              (l) =>
                                selectedStandardizeLineIds.has(l.lineId) &&
                                l.hasChange,
                            ).length
                          } dòng)`}
                    </Button>
                  </div>
                </div>
              </section>

              <section className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
                <table className="min-w-full text-left text-xs">
                  <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-center w-10">
                        <input
                          type="checkbox"
                          aria-label="Chọn tất cả dòng chuẩn hóa"
                          checked={
                            standardizedPreviewLines.filter(
                              (item) => item.confidence !== 'UNKNOWN' && item.hasChange,
                            ).length > 0 &&
                            standardizedPreviewLines
                              .filter(
                                (item) =>
                                  item.confidence !== 'UNKNOWN' && item.hasChange,
                              )
                              .every((item) =>
                                selectedStandardizeLineIds.has(item.lineId),
                              )
                          }
                          onChange={handleToggleAllStandardize}
                        />
                      </th>
                      <th className="px-3 py-2 w-12">STT</th>
                      <th className="px-3 py-2">Phân loại</th>
                      <th className="px-3 py-2">Tên gốc trong Excel</th>
                      <th className="px-3 py-2">Tên chuẩn đề xuất</th>
                      <th className="px-3 py-2">Mức tin cậy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standardizedPreviewLines.map((item, idx) => {
                      const isSelected = selectedStandardizeLineIds.has(item.lineId)
                      const isUnknown = item.confidence === 'UNKNOWN'
                      const isConfident = item.confidence === 'CONFIDENT'

                      const kindLabel =
                        item.lineKind === 'wood'
                          ? 'Gỗ'
                          : item.lineKind === 'hardware'
                            ? 'Ngũ kim'
                            : 'Đóng gói'

                      let badgeTone: BadgeTone = 'neutral'
                      let badgeText = 'Không rõ'
                      if (isConfident) {
                        badgeTone = 'success'
                        badgeText = 'Chắc chắn'
                      } else if (item.confidence === 'NEEDS_REVIEW') {
                        badgeTone = 'warning'
                        badgeText = 'Cần xem'
                      } else {
                        badgeTone = 'warning'
                        badgeText = 'Chưa chuẩn hóa'
                      }

                      return (
                        <tr
                          key={item.lineId}
                          className={`border-t border-slate-100 ${
                            isSelected ? 'bg-indigo-50/30' : ''
                          }`}
                        >
                          <td className="px-3 py-1.5 text-center">
                            <input
                              type="checkbox"
                              aria-label={`Chọn dòng ${item.rawName}`}
                              disabled={isUnknown || !item.hasChange}
                              checked={isSelected}
                              onChange={() => handleToggleStandardizeLine(item.lineId)}
                            />
                          </td>
                          <td className="px-3 py-1.5 text-slate-400 font-mono">
                            {idx + 1}
                          </td>
                          <td className="px-3 py-1.5 text-slate-600">
                            {kindLabel}
                          </td>
                          <td className="px-3 py-1.5 font-medium text-slate-900">
                            {item.rawName || '—'}
                          </td>
                          <td className="px-3 py-1.5 font-medium text-indigo-700">
                            {item.hasChange ? (
                              <span>{item.proposedName}</span>
                            ) : (
                              <span className="text-slate-400 font-normal">
                                (Đã đúng tên chuẩn)
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-1.5">
                            <div className="flex items-center gap-1.5">
                              <StatusBadge tone={badgeTone} label={badgeText} />
                              {isUnknown ? (
                                <span className="text-amber-600 text-[11px] italic">
                                  Giữ nguyên tên gốc khi commit
                                </span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </section>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
